import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFieldDecimalPlacesConfig } from 'hooks';
import { useTranslation } from 'react-i18next';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import CheckIcon from '@mui/icons-material/Check';
import { isDirty as checkDirty } from '@totalsoft/change-tracking';
import { ThinCheckbox, StyledTableBodyCell, StyledGrayIcon, StyledTableRow } from 'components/common/Styles';
import { Autocomplete, IconButton, TextField, Typography } from '@totalsoft/rocket-ui';
import { onCheckBoxChange } from 'utils/propertyChangeAdapters';
import { isValid, getErrors, useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { insuranceConventionRiskValidator } from 'features/insuranceConventions/edit/validators';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_INSURANCE_CONVENTIONS_WRITE } from 'constants/rightsCodes';
import TextTableCell from 'components/common/table/TextTableCell';
import FlexBox from 'components/common/FlexBox';
import { useGlobalIsDirty } from 'features/common/dirtyInfo/hooks';

const InsuranceConventionRiskContainer = ({
  risk,
  insuranceTypes,
  riskTypes,
  onUpdateLocalRisk,
  onMutateRisk,
  onDeleteRisk,
  onCancelAdd
}) => {
  const { cvsChPercent, cvsChAmount } = useFieldDecimalPlacesConfig();
  const [validation, validate] = useDirtyFieldValidation(insuranceConventionRiskValidator);
  const editing = useMemo(() => risk.editing || risk.isNew, [risk.editing, risk.isNew]);
  const disabled = useMemo(() => risk.disabled, [risk.disabled]);
  const canWrite = usePermission(ADMIN_INSURANCE_CONVENTIONS_WRITE);

  const updateLocalRisk = useCallback(
    (property, value) => onUpdateLocalRisk(risk.insuranceTypeId, risk.riskTypeId, property, value),
    [onUpdateLocalRisk, risk]
  );

  const handleMutateRisk = useCallback(
    risk => {
      if (!validate(risk)) return;
      onMutateRisk(risk);
    },
    [onMutateRisk, validate]
  );

  const handleEdit = useCallback(() => updateLocalRisk('editing', true), [updateLocalRisk]);

  const handleCancelEdit = useCallback(() => {
    if (risk.isNew) onCancelAdd();
    else updateLocalRisk('editing', false);
  }, [risk, onCancelAdd, updateLocalRisk]);

  const handleDeleteRisk = useCallback(() => {
    onDeleteRisk(risk.productId, risk.insuranceTypeId, risk.riskTypeId);
  }, [risk, onDeleteRisk]);

  return (
    <StyledTableRow>
      {editing ? (
        <RiskEditView
          risk={risk}
          validation={validation}
          insuranceTypes={insuranceTypes}
          riskTypes={riskTypes}
          disabled={disabled || !canWrite}
          formatPercent={cvsChPercent}
          formatAmount={cvsChAmount}
          onCancelEdit={handleCancelEdit}
          onMutateRisk={handleMutateRisk}
          onDeleteRisk={handleDeleteRisk}
        />
      ) : (
        <RiskDetailsView
          risk={risk}
          disabled={disabled || !canWrite}
          formatPercent={cvsChPercent}
          formatAmount={cvsChAmount}
          onEdit={handleEdit}
          onDeleteRisk={handleDeleteRisk}
        />
      )}
    </StyledTableRow>
  );
};

const RiskDetailsView = ({ risk, disabled, formatPercent, formatAmount, onEdit, onDeleteRisk }) => {
  const { t } = useTranslation();

  const getBooleanIcon = useCallback(value => (value ? <CheckIcon color='success' /> : <StyledGrayIcon>close</StyledGrayIcon>), []);

  return (
    <>
      <StyledTableBodyCell>
        <Typography>{risk.insuranceTypeName}</Typography>
      </StyledTableBodyCell>
      <TextTableCell content={risk.riskTypeName} sx={{ px: 3, py: 0 }} />
      <StyledTableBodyCell>{risk.riskPercent?.toFixed(formatPercent)}</StyledTableBodyCell>
      <StyledTableBodyCell>{risk.riskAmount?.toFixed(formatAmount)}</StyledTableBodyCell>
      <StyledTableBodyCell align='center'>{getBooleanIcon(risk.defaultYN)}</StyledTableBodyCell>
      <StyledTableBodyCell align='center'>{getBooleanIcon(risk.mandatoryYN)}</StyledTableBodyCell>
      <StyledTableBodyCell align='center'>{getBooleanIcon(risk.readOnlyYN)}</StyledTableBodyCell>
      <StyledTableBodyCell>
        <FlexBox gap={0.5} justifyContent='end'>
          <IconButton type='edit' color='primary' size='tiny' variant='text' onClick={onEdit} tooltip={t('Edit')} disabled={disabled} />
          <IconButton
            type='delete'
            color='primary'
            size='tiny'
            variant='text'
            onClick={onDeleteRisk}
            tooltip={t('Delete')}
            disabled={disabled}
          />
        </FlexBox>
      </StyledTableBodyCell>
    </>
  );
};

RiskDetailsView.propTypes = {
  risk: PropTypes.object,
  disabled: PropTypes.bool,
  formatPercent: PropTypes.number,
  formatAmount: PropTypes.number,
  onEdit: PropTypes.func,
  onDeleteRisk: PropTypes.func
};

const RiskEditView = ({
  risk,
  validation,
  insuranceTypes,
  riskTypes,
  disabled,
  formatPercent,
  formatAmount,
  onMutateRisk,
  onDeleteRisk,
  onCancelEdit
}) => {
  const { t } = useTranslation();

  const getLensValue = useCallback(
    () => ({
      ...risk,
      initialInsuranceTypeId: risk.insuranceTypeId,
      initialRiskTypeId: risk.riskTypeId,
      defaultYN: risk.defaultYN || false,
      mandatoryYN: risk.mandatoryYN || false,
      readOnlyYN: risk.readOnlyYN || false
    }),
    [risk]
  );

  const [lens, dirtyInfo] = useChangeTrackingLens(getLensValue());
  const isDirty = useMemo(() => checkDirty(dirtyInfo), [dirtyInfo]);

  useGlobalIsDirty(isDirty);

  return (
    <>
      <StyledTableBodyCell>
        {
          <Autocomplete
            label={t('InsuranceConventions.Details.Risks.InsuranceType')}
            simpleValue
            required
            value={lens.insuranceTypeId |> get}
            onChange={lens.insuranceTypeId |> set}
            options={insuranceTypes}
            error={!isValid(validation.insuranceTypeId)}
            helperText={getErrors(validation.insuranceTypeId)}
            disabled={disabled}
          />
        }
      </StyledTableBodyCell>
      <StyledTableBodyCell>
        {
          <Autocomplete
            label={t('InsuranceConventions.Details.Risks.Risk')}
            simpleValue
            required
            value={lens.riskTypeId |> get}
            onChange={lens.riskTypeId |> set}
            options={riskTypes}
            error={!isValid(validation.riskTypeId)}
            helperText={getErrors(validation.riskTypeId)}
            disabled={disabled}
          />
        }
      </StyledTableBodyCell>
      <StyledTableBodyCell>
        {
          <TextField
            label={t('InsuranceConventions.Details.Risks.RiskPercent')}
            isNumeric
            fullWidth
            decimalScale={formatPercent}
            value={(lens.riskPercent |> get) || ''}
            onChange={lens.riskPercent |> set}
            disabled={disabled}
          />
        }
      </StyledTableBodyCell>
      <StyledTableBodyCell>
        {
          <TextField
            label={t('InsuranceConventions.Details.Risks.RiskAmount')}
            isNumeric
            fullWidth
            decimalScale={formatAmount}
            value={(lens.riskAmount |> get) || ''}
            onChange={lens.riskAmount |> set}
            disabled={disabled}
          />
        }
      </StyledTableBodyCell>
      <StyledTableBodyCell align='center'>
        <ThinCheckbox checked={lens.defaultYN |> get} onChange={lens.defaultYN |> set |> onCheckBoxChange} disabled={disabled} />
      </StyledTableBodyCell>
      <StyledTableBodyCell align='center'>
        <ThinCheckbox checked={lens.mandatoryYN |> get} onChange={lens.mandatoryYN |> set |> onCheckBoxChange} disabled={disabled} />
      </StyledTableBodyCell>
      <StyledTableBodyCell align='center'>
        <ThinCheckbox checked={lens.readOnlyYN |> get} onChange={lens.readOnlyYN |> set |> onCheckBoxChange} disabled={disabled} />
      </StyledTableBodyCell>
      <StyledTableBodyCell>
        <FlexBox gap={0.5} justifyContent='end'>
          <IconButton
            type='save'
            color='primary'
            size='tiny'
            variant='text'
            onClick={() => {
              onMutateRisk(lens |> get);
            }}
            tooltip={t('Save')}
            disabled={disabled || !isDirty}
          />
          <IconButton
            type='cancel'
            color='primary'
            size='tiny'
            variant='text'
            onClick={onCancelEdit}
            tooltip={t('Cancel')}
            disabled={disabled}
          />
          {!risk.isNew && (
            <IconButton
              type='delete'
              color='primary'
              size='tiny'
              variant='text'
              onClick={onDeleteRisk}
              tooltip={t('Delete')}
              disabled={disabled}
            />
          )}
        </FlexBox>
      </StyledTableBodyCell>
    </>
  );
};

RiskEditView.propTypes = {
  risk: PropTypes.object,
  validation: PropTypes.object,
  insuranceTypes: PropTypes.array,
  riskTypes: PropTypes.array,
  disabled: PropTypes.bool,
  formatPercent: PropTypes.number,
  formatAmount: PropTypes.number,
  onMutateRisk: PropTypes.func,
  onDeleteRisk: PropTypes.func,
  onCancelEdit: PropTypes.func
};

InsuranceConventionRiskContainer.propTypes = {
  risk: PropTypes.object,
  insuranceTypes: PropTypes.array,
  riskTypes: PropTypes.array,
  onUpdateLocalRisk: PropTypes.func,
  onMutateRisk: PropTypes.func,
  onDeleteRisk: PropTypes.func,
  onCancelAdd: PropTypes.func
};

export default InsuranceConventionRiskContainer;
